import { formatMoney } from '@trade/money';
import { TradeQty } from '@trade/qty';

/**
 * @typedef {object} NormalizedCartItem
 * @property {number} variant_id
 * @property {number} quantity
 * @property {string} [key]
 * @property {number} [price]
 * @property {number} [original_line_price]
 * @property {number} [product_id]
 */

/**
 * @typedef {object} CartLine
 * @property {string} variantId
 * @property {number} quantity
 * @property {number} [pending]
 * @property {string} [lineKey]
 * @property {string} [productId]
 * @property {number} [unitPriceCents]
 */

/**
 * @typedef {object} MoneyConfig
 * @property {string} format
 * @property {string} currency
 * @property {string} [perItemSuffix]
 */

/** @type {MoneyConfig | null} */
let cachedMoneyConfig = null;

/**
 * @returns {MoneyConfig}
 */
export function getMoneyConfig() {
  if (cachedMoneyConfig) return cachedMoneyConfig;

  const source =
    document.querySelector('trade-collection[data-money-format]') ??
    document.querySelector('[data-trade-money-format]');

  const format =
    (source instanceof HTMLElement && source.dataset.moneyFormat) ||
    (typeof Theme !== 'undefined' && Theme.moneyFormat) ||
    '{{amount}}';
  const currency =
    (source instanceof HTMLElement && source.dataset.currency) ||
    (typeof Theme !== 'undefined' && Theme.currency) ||
    'USD';
  const perItemSuffix =
    (typeof Theme !== 'undefined' && Theme.translations?.quantity_per_item) || '';

  cachedMoneyConfig = { format, currency, perItemSuffix };
  return cachedMoneyConfig;
}

/**
 * @param {unknown} items
 * @returns {NormalizedCartItem[]}
 */
export function normalizeCartItems(items) {
  if (!Array.isArray(items)) return [];

  return items.map((item) => {
    const record = /** @type {Record<string, unknown>} */ (item);
    const merchandise = /** @type {Record<string, unknown> | undefined} */ (record.merchandise);

    return {
      variant_id: Number(record.variant_id ?? record.id ?? merchandise?.id),
      quantity: Number(record.quantity ?? 0),
      key: typeof record.key === 'string' ? record.key : undefined,
      price: record.price != null ? Number(record.price) : undefined,
      original_line_price:
        record.original_line_price != null
          ? Number(record.original_line_price)
          : record.line_price != null
            ? Number(record.line_price)
            : undefined,
      product_id: record.product_id != null ? Number(record.product_id) : undefined,
    };
  });
}

/**
 * @param {NormalizedCartItem[]} items
 * @param {number} variantId
 * @returns {NormalizedCartItem | undefined}
 */
export function findCartItemForVariant(items, variantId) {
  const matches = items.filter((item) => item.variant_id === variantId);
  if (matches.length === 0) return undefined;

  const quantity = matches.reduce((sum, item) => sum + item.quantity, 0);
  const primary = matches[0];

  return {
    variant_id: variantId,
    quantity,
    key: primary.key,
    price: primary.price,
    original_line_price: matches.reduce((sum, item) => sum + (item.original_line_price ?? 0), 0),
    product_id: primary.product_id,
  };
}

/**
 * @param {HTMLElement} qtyEl
 */
function shouldSkipQtyInputSync(qtyEl) {
  return TradeQty.isRecentlyEdited(qtyEl);
}

/**
 * @param {HTMLElement} row
 * @param {number} quantity
 * @param {{ commit?: boolean }} [options]
 */
function setRowQuantityInputs(row, quantity, options = {}) {
  const commit = options.commit !== false;

  for (const qtyEl of row.querySelectorAll('trade-qty')) {
    if (!(qtyEl instanceof HTMLElement)) continue;
    if (shouldSkipQtyInputSync(qtyEl)) continue;

    const input = qtyEl.querySelector('input[data-cart-quantity]');
    if (!(input instanceof HTMLInputElement)) continue;
    if (commit) {
      input.setAttribute('data-cart-quantity', String(quantity));
    }
    input.value = String(quantity);
    if ('setValue' in qtyEl && typeof qtyEl.setValue === 'function') {
      qtyEl.setValue(quantity);
    }
  }
}

/**
 * @param {HTMLElement} row
 * @returns {number}
 */
export function getUnitPriceCentsForRow(row) {
  const source = row.matches('[data-unit-price-cents]')
    ? row
    : row.closest('[data-unit-price-cents]');

  if (source instanceof HTMLElement) {
    const cents = parseInt(source.dataset.unitPriceCents || '', 10);
    if (!isNaN(cents)) return cents;
  }

  return 0;
}

/**
 * @param {ParentNode} root
 * @param {string | number} [variantId]
 * @returns {HTMLElement[]}
 */
export function queryVariantRows(root, variantId) {
  const selector =
    variantId != null ? `[data-variant-id="${String(variantId)}"]` : '[data-variant-id]';

  return Array.from(root.querySelectorAll(selector)).filter((el) => el instanceof HTMLElement);
}

/**
 * @param {string | number} variantId
 * @param {CartLine | undefined} line
 */
export function presentVariant(variantId, line) {
  const quantity = line?.pending ?? line?.quantity ?? 0;
  const committed = line?.quantity ?? 0;
  const isPending = line?.pending != null && line.pending !== committed;

  const unitPrice =
    line?.unitPriceCents ??
    getUnitPriceCentsForRow(queryVariantRows(document, variantId)[0] ?? document.body);

  /** @type {NormalizedCartItem | null} */
  const cartItem =
    quantity > 0
      ? {
          variant_id: Number(variantId),
          quantity,
          key: line?.lineKey,
          price: unitPrice,
          original_line_price: unitPrice * quantity,
        }
      : null;

  for (const row of queryVariantRows(document, variantId)) {
    if (isPending) {
      row.classList.add('trade-variant--pending', 'trade-variant-table__row--pending');
      syncVariantRow(row, cartItem, quantity, { commit: false });
    } else {
      row.classList.remove('trade-variant--pending', 'trade-variant-table__row--pending');
      syncVariantRow(row, cartItem, quantity, { commit: true });
    }
  }
}

/**
 * @param {string} productId
 * @param {CartLine[]} lines
 */
export function presentProductFooter(productId, lines) {
  for (const card of document.querySelectorAll(`[data-product-id="${productId}"]`)) {
    if (!(card instanceof HTMLElement)) continue;
    updateProductFooter(card, lines);
  }
}

/**
 * @param {ParentNode} root
 * @param {{ get: (variantId: string) => CartLine | undefined; getProductLines: (productId: string) => CartLine[] }} store
 */
export function hydrateRoot(root, store) {
  /** @type {Set<string>} */
  const productIds = new Set();

  for (const row of queryVariantRows(root)) {
    const variantId = row.dataset.variantId;
    if (!variantId) continue;

    presentVariant(variantId, store.get(variantId));

    const productCard = row.closest('[data-product-id]');
    if (productCard instanceof HTMLElement && productCard.dataset.productId) {
      productIds.add(productCard.dataset.productId);
    }
  }

  for (const productId of productIds) {
    presentProductFooter(productId, store.getProductLines(productId));
  }
}

/**
 * @param {HTMLElement} row
 * @param {NormalizedCartItem | null} cartItem
 * @param {number} quantity
 * @param {{ commit?: boolean }} [options]
 */
function syncVariantRow(row, cartItem, quantity, options = {}) {
  setRowQuantityInputs(row, quantity, options);

  if (cartItem?.key) {
    row.dataset.cartLineKey = cartItem.key;
  } else if (quantity === 0) {
    delete row.dataset.cartLineKey;
  }

  for (const removeButton of row.querySelectorAll('[data-trade-remove-variant]')) {
    if (!(removeButton instanceof HTMLElement)) continue;
    removeButton.classList.toggle('trade-variant__remove--hidden', quantity === 0);
    if (quantity === 0) {
      removeButton.setAttribute('tabindex', '-1');
      removeButton.setAttribute('aria-hidden', 'true');
    } else {
      removeButton.removeAttribute('tabindex');
      removeButton.removeAttribute('aria-hidden');
    }
  }

  const { format, currency, perItemSuffix } = getMoneyConfig();
  const lineTotal = quantity === 0 ? 0 : (cartItem?.original_line_price ?? 0);
  const formattedTotal = formatMoney(lineTotal, format, currency);

  for (const totalEl of row.querySelectorAll('[data-trade-line-total]')) {
    if (totalEl instanceof HTMLElement) {
      totalEl.textContent = formattedTotal;
    }
  }

  row.classList.toggle('trade-variant--selected', quantity > 0);
  row.classList.toggle('trade-variant-table__row--selected', quantity > 0);

  for (const lineTotalEl of row.querySelectorAll('.trade-variant__mobile-line-total')) {
    if (!(lineTotalEl instanceof HTMLElement)) continue;
    lineTotalEl.classList.toggle('trade-variant__mobile-line-total--hidden', quantity === 0);
  }

  if (cartItem?.price != null && quantity > 0) {
    const unitFormatted = formatMoney(cartItem.price, format, currency) + (perItemSuffix || '');
    for (const priceEl of row.querySelectorAll('.trade-variant__price .price, .trade-variant__mobile-price')) {
      if (priceEl instanceof HTMLElement) {
        priceEl.textContent = unitFormatted;
      }
    }
  }
}

/**
 * @param {HTMLElement} component
 * @param {CartLine[]} lines
 */
function updateProductFooter(component, lines) {
  const unitsEl = component.querySelector('[data-trade-selected-units]');
  const variantsEl = component.querySelector('[data-trade-selected-variants]');

  let units = 0;
  let variants = 0;
  let totalCents = 0;

  for (const line of lines) {
    const qty = line.pending ?? line.quantity;
    if (qty <= 0) continue;

    units += qty;
    variants += 1;
    const unitPrice = line.unitPriceCents ?? 0;
    totalCents += unitPrice * qty;
  }

  if (unitsEl instanceof HTMLElement) {
    const template = unitsEl.dataset.tradeSelectedUnitsTemplate || 'Selected: __UNITS__ units';
    unitsEl.textContent = template.replace('__UNITS__', String(units));
  }

  if (variantsEl instanceof HTMLElement) {
    const template = variantsEl.dataset.tradeSelectedVariantsTemplate || '__VARIANTS__ variants';
    variantsEl.textContent = template.replace('__VARIANTS__', String(variants));
  }

  const { format, currency } = getMoneyConfig();
  const formattedTotal = formatMoney(totalCents, format, currency);

  const totalEl = component.querySelector('[data-trade-total]');
  if (totalEl instanceof HTMLElement) {
    totalEl.textContent = formattedTotal;
  }

  const totalItemsEl = component.querySelector('[data-trade-total-items]');
  if (totalItemsEl instanceof HTMLElement) {
    totalItemsEl.textContent = String(units);
  }

  const removeAllButton = component.querySelector('[data-trade-remove-all]');
  if (removeAllButton instanceof HTMLElement) {
    const hasUnits = units > 0;
    removeAllButton.classList.toggle('hidden', !hasUnits);
    if (hasUnits) {
      removeAllButton.removeAttribute('hidden');
    } else {
      removeAllButton.setAttribute('hidden', '');
    }
  }
}
